import { createContext, useState, useMemo, useCallback, type ReactNode } from 'react'
import { AtpAgent } from '@atproto/api'
import { useQueryClient } from '@tanstack/react-query'
import { BLUESKY_PUBLIC_API, BLACKSKY_PUBLIC_API, BLACKSKY_APPVIEW_DID } from '@/constants/api'

export type AppView = 'bluesky' | 'blacksky'

export const STORAGE_KEY = 'mackinac:appView'

const APP_VIEW_URLS: Record<AppView, string> = {
    bluesky: BLUESKY_PUBLIC_API,
    blacksky: BLACKSKY_PUBLIC_API,
}

/** DID of the AppView to use for service-auth proxy on authenticated reads. Null = default (Bluesky). */
const APP_VIEW_DIDS: Record<AppView, string | null> = {
    bluesky: null,
    blacksky: BLACKSKY_APPVIEW_DID,
}

export function getStoredAppView(): AppView {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored === 'bluesky' || stored === 'blacksky') return stored
    } catch {
        // localStorage unavailable (privacy mode, SSR, etc.)
    }
    return 'bluesky'
}

export interface AppViewContextType {
    appView: AppView
    setAppView: (view: AppView) => void
    publicAgent: AtpAgent
    /** DID for service-auth proxy on authenticated reads (e.g. getTimeline). Null when using the default (Bluesky) AppView. */
    appViewDid: string | null
}

export const AppViewContext = createContext<AppViewContextType | undefined>(undefined)

export function AppViewProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()
    const [appView, setAppViewState] = useState<AppView>(getStoredAppView)

    const setAppView = useCallback(
        (view: AppView) => {
            try {
                localStorage.setItem(STORAGE_KEY, view)
            } catch {
                // localStorage unavailable
            }
            setAppViewState(view)

            // Invalidate queries that use the app view agent for fetching
            void queryClient.invalidateQueries({ queryKey: ['timeline'] })
            void queryClient.invalidateQueries({ queryKey: ['posts'] })
            void queryClient.invalidateQueries({ queryKey: ['viewProfile'] })
            void queryClient.invalidateQueries({ queryKey: ['profile'] })
            void queryClient.invalidateQueries({ queryKey: ['thread'] })
            void queryClient.invalidateQueries({ queryKey: ['search'] })
            void queryClient.invalidateQueries({ queryKey: ['hashtag'] })
            void queryClient.invalidateQueries({ queryKey: ['quote-posts'] })
            void queryClient.invalidateQueries({ queryKey: ['newPostsCheck'] })
        },
        [queryClient]
    )

    const publicAgent = useMemo(() => new AtpAgent({ service: APP_VIEW_URLS[appView] }), [appView])
    const appViewDid = useMemo(() => APP_VIEW_DIDS[appView], [appView])

    const value = useMemo(
        () => ({ appView, setAppView, publicAgent, appViewDid }),
        [appView, setAppView, publicAgent, appViewDid]
    )

    return <AppViewContext.Provider value={value}>{children}</AppViewContext.Provider>
}
